import { useEffect, useState } from "react";
import { View, Text, SafeAreaView, ScrollView, TouchableOpacity, ActivityIndicator, Share, Platform, Linking } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { getDog, getDogStats, getRankings } from "../../lib/api";
import { DogTradingCard } from "../../../components/DogTradingCard";
import type { Dog, DogStats, BreedRanking } from "../../lib/types";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";

export default function DogCardScreen() {
  const { dogId } = useLocalSearchParams<{ dogId: string }>();
  const router = useRouter();
  const [dog, setDog] = useState<Dog | null>(null);
  const [stats, setStats] = useState<DogStats | null>(null);
  const [rankings, setRankings] = useState<BreedRanking[]>([]);
  const [projectedRank, setProjectedRank] = useState<number | null>(null);
  const [officialRank, setOfficialRank] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!dogId) return;
    (async () => {
      try {
        // Try public endpoint first — works without auth when the dog's profile is public.
        const publicRes = await fetch(`${API_URL}/public/dogs/${encodeURIComponent(dogId)}`);
        if (publicRes.ok) {
          const payload = await publicRes.json();
          setDog(payload.dog);
          setStats(payload.stats);
          setRankings(payload.rankings || []);
          setProjectedRank(payload.projectedRank?.rank ?? null);
          setOfficialRank(payload.officialRank ?? null);
          return;
        }

        // Fallback: auth-gated endpoints (owner viewing their own private dog).
        const [d, s] = await Promise.all([getDog(dogId), getDogStats(dogId)]);
        setDog(d);
        setStats(s);
        if (d?.breed) {
          const r = await getRankings(d.breed, String(new Date().getFullYear()));
          setRankings(r);
          // Match registered name against rankings to compute official + projected here too.
          const norm = (x: string) => x.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();
          if (d.registeredName) {
            const match = r.find((row) => norm(row.dogName).includes(norm(d.registeredName!)));
            setOfficialRank(match?.rank ?? null);
          }
          const speed = s?.bestThreeAvgSpeed ?? 0;
          if (speed > 0 && r.length > 0) {
            let rank = 1;
            for (const row of r) if (row.speed != null && row.speed > speed) rank++;
            setProjectedRank(rank);
          }
        }
      } catch (err: any) {
        setErrorMsg(err?.message || "Couldn't load this card");
      } finally {
        setLoading(false);
      }
    })();
  }, [dogId]);

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#0F1729", justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#F5A623" />
      </SafeAreaView>
    );
  }

  if (!dog) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#0F1729", justifyContent: "center", alignItems: "center", padding: 24 }}>
        <Ionicons name="lock-closed-outline" size={48} color="#94A3B8" />
        <Text style={{ color: "#fff", fontSize: 18, fontWeight: "700", marginTop: 14, textAlign: "center" }}>
          This card isn't available
        </Text>
        <Text style={{ color: "#94A3B8", fontSize: 14, marginTop: 8, textAlign: "center", maxWidth: 320 }}>
          {errorMsg ?? "The owner may have set this profile to private. Ask them to share again once it's public."}
        </Text>
        <TouchableOpacity onPress={() => router.replace("/" as any)} style={{ marginTop: 22, backgroundColor: "#F5A623", paddingHorizontal: 22, paddingVertical: 12, borderRadius: 10 }}>
          <Text style={{ color: "#1B2A4A", fontWeight: "700" }}>Go to Dash-Hound</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#0F1729" }}>
      {/* Back nav */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14 }}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace("/" as any)} style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
          <Text style={{ color: "#fff", fontSize: 16, fontWeight: "600" }}>Back</Text>
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={{ flexGrow: 1, alignItems: "center", justifyContent: "center", padding: 20, paddingBottom: 60 }}>
        <Text style={{ color: "#94A3B8", fontSize: 13, fontWeight: "600", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>
          Dash-Hound Trading Card
        </Text>
        <Text style={{ color: "#fff", fontSize: 22, fontWeight: "800", marginBottom: 28, textAlign: "center" }}>
          {dog.name}
        </Text>

        <DogTradingCard
          dog={dog}
          stats={stats}
          akcRank={officialRank}
          projectedRank={projectedRank}
          totalInBreed={rankings.length || null}
          topBreedSpeed={rankings[0]?.speed ?? null}
        />

        <ShareRow dog={dog} />

        <Text style={{ color: "#94A3B8", fontSize: 12, marginTop: 14, textAlign: "center", maxWidth: 320 }}>
          Tap the card to flip it.
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

// Share buttons row — system share sheet + per-platform quick actions.
function ShareRow({ dog }: { dog: Dog }) {
  const [copied, setCopied] = useState(false);
  const url = `https://dashhound.app/dogs/${dog.id}/card`;
  const text = `Check out ${dog.name}'s Dash-Hound Trading Card`;
  const shareText = `${text} — ${url}`;

  async function handleNativeShare() {
    try {
      if (Platform.OS === "web") {
        // Web Share API is available on most mobile browsers + macOS Safari
        if (typeof navigator !== "undefined" && (navigator as any).share) {
          await (navigator as any).share({ title: text, text, url });
          return;
        }
        // Fall back to copy if Web Share API not available (most desktop browsers)
        await copyLink();
        return;
      }
      await Share.share({ message: shareText, url });
    } catch {
      // User cancelled — no-op
    }
  }

  async function copyLink() {
    try {
      if (Platform.OS === "web") {
        await navigator.clipboard.writeText(url);
      } else {
        // RN doesn't ship a clipboard primitive — use the share sheet as fallback for native copy
        await Share.share({ message: url, url });
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  function openExternal(target: string) {
    if (Platform.OS === "web") {
      window.open(target, "_blank", "noopener,noreferrer");
    } else {
      Linking.openURL(target).catch(() => {});
    }
  }

  const enc = encodeURIComponent;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
  const smsUrl = Platform.OS === "ios" ? `sms:&body=${enc(shareText)}` : `sms:?body=${enc(shareText)}`;
  const mailUrl = `mailto:?subject=${enc(text)}&body=${enc(shareText)}`;

  return (
    <View style={{ marginTop: 22, alignItems: "center", width: "100%", maxWidth: 360 }}>
      <Text style={{ color: "#94A3B8", fontSize: 11, fontWeight: "700", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>Share This Card</Text>

      {/* Primary share — system share sheet */}
      <TouchableOpacity
        onPress={handleNativeShare}
        style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8, backgroundColor: "#F5A623", paddingVertical: 12, paddingHorizontal: 22, borderRadius: 10, width: "100%" }}
      >
        <Ionicons name="share-outline" size={18} color="#1B2A4A" />
        <Text style={{ color: "#1B2A4A", fontWeight: "800", fontSize: 15 }}>Share</Text>
      </TouchableOpacity>

      {/* Quick-action icon row */}
      <View style={{ flexDirection: "row", justifyContent: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <ShareIcon icon="link-outline" label={copied ? "Copied!" : "Copy"} onPress={copyLink} />
        <ShareIcon icon="logo-facebook" label="Facebook" onPress={() => openExternal(fbUrl)} />
        <ShareIcon label="X" onPress={() => openExternal(xUrl)} customIcon={<Text style={{ color: "#fff", fontSize: 18, fontWeight: "900", fontFamily: Platform.OS === "web" ? "Arial Black, sans-serif" : undefined }}>𝕏</Text>} />
        <ShareIcon icon="chatbubble-outline" label="Text" onPress={() => openExternal(smsUrl)} />
        <ShareIcon icon="mail-outline" label="Email" onPress={() => openExternal(mailUrl)} />
      </View>
    </View>
  );
}

function ShareIcon({ icon, label, onPress, brandColor, customIcon }: { icon?: React.ComponentProps<typeof Ionicons>["name"]; label: string; onPress: () => void; brandColor?: string; customIcon?: React.ReactNode }) {
  return (
    <TouchableOpacity onPress={onPress} style={{ alignItems: "center", width: 60 }}>
      <View
        style={{
          width: 44, height: 44, borderRadius: 22,
          backgroundColor: "rgba(255,255,255,0.08)",
          borderWidth: 1, borderColor: "rgba(255,255,255,0.15)",
          alignItems: "center", justifyContent: "center",
        }}
      >
        {customIcon ? customIcon : icon ? <Ionicons name={icon} size={20} color={brandColor ?? "#fff"} /> : null}
      </View>
      <Text style={{ color: "#94A3B8", fontSize: 10, marginTop: 4, fontWeight: "600" }} numberOfLines={1}>{label}</Text>
    </TouchableOpacity>
  );
}
